/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentProps, Ref } from 'preact';
import { forwardRef } from 'preact/compat';
import { useImperativeHandle, useRef } from 'preact/hooks';
import { BundleType } from '../resources/nls/bundle';
import { CalendarIcon } from '#PRIVATE_ThemedIcons/CalendarIcon';
import {
  CalendarDate,
  CalendarDatePlaceholders,
  InputDateMask,
  InputDateMaskContext
} from '#UNSAFE_InputDateMask';
import { DatePicker } from '#UNSAFE_DatePicker';
import { HiddenAccessible } from '#UNSAFE_HiddenAccessible';
import { IconButton } from '#UNSAFE_IconButton';
import { InputDatePickerDropdown } from './InputDatePickerDropdown';
import { FocusableHandle } from '#hooks/UNSAFE_useFocusableTextField';
import { merge } from '@oracle/oraclejet-internal-utilities/stringUtils';
import { useFormContext } from '#hooks/UNSAFE_useFormContext';
import { useId } from '#hooks/UNSAFE_useId';
import { useInputDatePicker } from './useInputDatePicker';
import { useTestId } from '#hooks/UNSAFE_useTestId';
import { useTranslationBundle } from '#hooks/UNSAFE_useTranslationBundle';
import { ValueUpdateDetail } from '#utils/UNSAFE_valueUpdateDetail';

type InputDateMaskProps = ComponentProps<typeof InputDateMask>;
type PickedPropsFromInputDateMask = Pick<
  InputDateMaskProps,
  | 'aria-describedby'
  | 'assistiveText'
  | 'columnSpan'
  | 'helpSourceLink'
  | 'helpSourceText'
  | 'isDisabled'
  | 'isReadonly'
  | 'isRequired'
  | 'isRequiredShown'
  | 'label'
  | 'labelEdge'
  | 'labelStartWidth'
  | 'messages'
  | 'textAlign'
  | 'testId'
  | 'userAssistanceDensity'
  | 'variant'
>;

type DatePickerProps = ComponentProps<typeof DatePicker>;
type PickedPropsFromDatePicker = Pick<
  DatePickerProps,
  | 'dayFormatter'
  | 'daysOutsideMonth'
  | 'max'
  | 'min'
  | 'monthAndYearPicker'
  | 'todayTimeZone'
  | 'todayButton'
  | 'weekDisplay'
>;

type Props = PickedPropsFromInputDateMask &
  PickedPropsFromDatePicker & {
    /**
     * If specified, overrides the locale specific order of the individual date segments
     * in the date field as well as the locale specific separator. The value of each placeholder
     * is only used when the type is 'literal', otherwise it is replaced with a translated
     * placeholder for month, day, or year. The placeholders must include month, day, and year.
     */
    masks?: CalendarDatePlaceholders;

    /**
     * The current display value of the component.
     * This is a CalendarDate, not CalendarDateRequired, because this is a controlled component and
     * the user may type the month, day, and year separately. If a selection is made in the date
     * picker, then the value is a complete date.
     */
    value?: CalendarDate;

    /**
     * Callback invoked when the user commits the entered value by blurring or hitting the enter key.
     * It is also called when a selection is made in the date picker.
     */
    onCommit?: (detail: ValueUpdateDetail<CalendarDate>) => void;

    /**
     * Callback invoked each time the user changes the value of a segment. For example, if the user
     * types '12' into the month segment, this callback will be called twice to change the month
     * first to '1' and then '12'.
     */
    onInput: (detail: ValueUpdateDetail<CalendarDate>) => void;
  };

/**
 * InputDatePicker allows users to enter or select a single date using a calendar interface.
 */
export const InputDatePicker = forwardRef(
  (
    {
      'aria-describedby': propDescribedBy,
      assistiveText,
      dayFormatter,
      daysOutsideMonth,
      helpSourceLink,
      helpSourceText,
      isDisabled,
      isReadonly,
      label,
      max,
      messages,
      min,
      monthAndYearPicker,
      todayTimeZone,
      todayButton,
      value,
      onCommit,
      onInput,
      testId,
      userAssistanceDensity: propUserAssistanceDensity,
      weekDisplay,
      ...dateMaskProps
    }: Props,
    ref?: Ref<FocusableHandle>
  ) => {
    const testIdProps = useTestId(testId);
    const rootRef = useRef<HTMLDivElement>(null);

    const {
      calendarIconActionHandler,
      datePickerCommitHandler,
      completeDateValue,
      dateValue,
      dropdownCloseHandler,
      iconRef,
      isOpen,
      maskCommitHandler,
      maskInputHandler,
      maskRef
    } = useInputDatePicker({
      isDisabled,
      isReadonly,
      onCommit,
      onInput,
      rootRef,
      value
    });

    const mainFieldRef = useRef<HTMLDivElement>(null);
    const { inputDatePicker_selectDate, inputDatePicker_instruction } =
      useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
    const calendarIconAriaLabel = inputDatePicker_selectDate();

    // We need this to determine if UA/messaging should be shown in the dropdown.
    const { userAssistanceDensity: formUserAssistanceDensity } = useFormContext();
    const userAssistanceDensity = propUserAssistanceDensity ?? formUserAssistanceDensity;

    useImperativeHandle(ref!, () => ({
      focus: () => {
        maskRef.current?.focus();
      },
      blur: () => {
        maskRef.current?.blur();
      }
    }));

    const maskFieldInstructionId = useId();
    const maskFieldInstructionText = inputDatePicker_instruction();
    const maskFieldInstruction =
      !isDisabled && !isReadonly ? (
        <HiddenAccessible id={maskFieldInstructionId} isHidden={true}>
          {maskFieldInstructionText}
        </HiddenAccessible>
      ) : undefined;
    const ariaDescribedBy =
      !isDisabled && !isReadonly
        ? merge([propDescribedBy, maskFieldInstructionId])
        : propDescribedBy;

    const endContent = (
      <IconButton
        aria-haspopup="dialog"
        aria-label={calendarIconAriaLabel}
        isDisabled={isDisabled}
        ref={iconRef}
        size="sm"
        testId={testId ? testId + '_calendarIcon' : undefined}
        variant="ghost"
        onAction={calendarIconActionHandler}>
        <CalendarIcon />
      </IconButton>
    );

    const shouldShowDropdown = isOpen as boolean;

    return (
      <div {...testIdProps} ref={rootRef}>
        <InputDateMaskContext.Provider
          value={{ isDropdownOpen: isOpen, isInputDatePickerParent: true }}>
          <InputDateMask
            aria-describedby={ariaDescribedBy}
            assistiveText={assistiveText}
            endContent={endContent}
            helpSourceLink={helpSourceLink}
            helpSourceText={helpSourceText}
            isDisabled={isDisabled}
            isReadonly={isReadonly}
            label={label}
            onCommit={maskCommitHandler}
            onInput={maskInputHandler}
            mainFieldRef={mainFieldRef}
            messages={messages}
            testId={testId ? testId + '_mask' : undefined}
            ref={maskRef}
            userAssistanceDensity={propUserAssistanceDensity}
            value={dateValue}
            {...dateMaskProps}
          />
        </InputDateMaskContext.Provider>

        {maskFieldInstruction}

        {shouldShowDropdown && (
          <InputDatePickerDropdown
            anchorRef={mainFieldRef}
            assistiveText={assistiveText}
            dayFormatter={dayFormatter}
            daysOutsideMonth={daysOutsideMonth}
            helpSourceLink={helpSourceLink}
            helpSourceText={helpSourceText}
            isDisabled={isDisabled}
            isReadonly={isReadonly}
            isOpen={isOpen}
            label={label}
            onClose={dropdownCloseHandler}
            onCommit={datePickerCommitHandler}
            max={max}
            messages={messages}
            min={min}
            monthAndYearPicker={monthAndYearPicker}
            testId={testId ? testId + '_datePicker' : undefined}
            todayTimeZone={todayTimeZone}
            todayButton={todayButton}
            userAssistanceDensity={userAssistanceDensity}
            value={completeDateValue}
            weekDisplay={weekDisplay}
          />
        )}
      </div>
    );
  }
);
